import React, { useState, useEffect, Fragment, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  ServerIcon,
  CloudIcon,
  InformationCircleIcon,
  CalendarIcon,
  MapPinIcon,
  FingerPrintIcon,
  CubeIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { toast } from 'react-hot-toast';

interface ResourceDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  resourceId: string;
}

interface Resource {
  id: string;
  name: string;
  resourceGroupName: string;
  resourceType: string;
  resourceId: string;
  cloudProvider: string;
  region?: string;
  status: string;
  ipAddress?: string;
  subnetId?: string;
  vpcId?: string;
  instanceType?: string;
  storageType?: string;
  size?: string;
  metadata?: any;
  syncedAt?: string;
  createdAt: string;
  updatedAt: string;
  Engagement?: {
    id: string;
    name: string;
    number: string;
  };
  ResourceActivity: Array<{
    id: string;
    activityType: string;
    description: string;
    performedAt: string;
    User: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
  }>;
}

const ResourceDetailModal: React.FC<ResourceDetailModalProps> = ({
  isOpen,
  onClose,
  resourceId
}) => {
  const [resource, setResource] = useState<Resource | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  const fetchResource = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/resources/${resourceId}`);
      setResource(response.data);
    } catch (error) {
      console.error('Error fetching resource:', error);
      toast.error('Failed to fetch resource details');
    } finally {
      setLoading(false);
    }
  }, [resourceId]);

  useEffect(() => {
    if (isOpen && resourceId) {
      fetchResource();
    }
  }, [isOpen, resourceId, fetchResource]);

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'running':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'inactive':
      case 'stopped':
        return <ExclamationCircleIcon className="h-5 w-5 text-yellow-500" />;
      case 'failed':
      case 'error':
        return <ExclamationCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <InformationCircleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'running':
        return 'bg-green-100 text-green-800';
      case 'inactive':
      case 'stopped':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
      case 'error':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const renderDetails = () => {
    if (!resource) return null;

    return (
      <div className="space-y-6">
        {/* Basic Information */}
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-4">Basic Information</h3>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex items-start space-x-3">
              <FingerPrintIcon className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <dt className="text-sm font-medium text-gray-500">Resource ID</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono">{resource.resourceId || 'N/A'}</dd>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CubeIcon className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <dt className="text-sm font-medium text-gray-500">Resource Type</dt>
                <dd className="mt-1 text-sm text-gray-900">{resource.resourceType || 'N/A'}</dd>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <ServerIcon className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <dt className="text-sm font-medium text-gray-500">Resource Group</dt>
                <dd className="mt-1 text-sm text-gray-900">{resource.resourceGroupName || 'N/A'}</dd>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CloudIcon className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <dt className="text-sm font-medium text-gray-500">Cloud Provider</dt>
                <dd className="mt-1 text-sm text-gray-900">{resource.cloudProvider || 'N/A'}</dd>
              </div>
            </div>
            {resource.region && (
              <div className="flex items-start space-x-3">
                <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <dt className="text-sm font-medium text-gray-500">Region</dt>
                  <dd className="mt-1 text-sm text-gray-900">{resource.region}</dd>
                </div>
              </div>
            )}
            <div className="flex items-start space-x-3">
              <CalendarIcon className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <dt className="text-sm font-medium text-gray-500">Last Synced</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatDateTime(resource.syncedAt)}</dd>
              </div>
            </div>
          </dl>
        </div>

        {/* Technical Details */}
        {(resource.ipAddress || resource.instanceType || resource.storageType || resource.size || resource.vpcId || resource.subnetId) && (
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-4">Technical Details</h3>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {resource.ipAddress && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">IP Address</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono">{resource.ipAddress}</dd>
                </div>
              )}
              {resource.instanceType && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Instance Type</dt>
                  <dd className="mt-1 text-sm text-gray-900">{resource.instanceType}</dd>
                </div>
              )}
              {resource.storageType && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Storage Type</dt>
                  <dd className="mt-1 text-sm text-gray-900">{resource.storageType}</dd>
                </div>
              )}
              {resource.size && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Size</dt>
                  <dd className="mt-1 text-sm text-gray-900">{resource.size}</dd>
                </div>
              )}
              {resource.vpcId && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">VPC ID</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono">{resource.vpcId}</dd>
                </div>
              )}
              {resource.subnetId && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Subnet ID</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono">{resource.subnetId}</dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {/* Metadata */}
        {resource.metadata && Object.keys(resource.metadata).length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-4">Additional Properties</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <dl className="space-y-2">
                {Object.entries(resource.metadata).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500">{key}:</dt>
                    <dd className="text-sm text-gray-900">{JSON.stringify(value)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderActivity = () => {
    if (!resource || !resource.ResourceActivity || resource.ResourceActivity.length === 0) {
      return (
        <div className="text-center py-8">
          <InformationCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm text-gray-500">No activity recorded</p>
        </div>
      );
    }

    return (
      <div className="flow-root">
        <ul className="-mb-8">
          {resource.ResourceActivity.map((activity, idx) => (
            <li key={activity.id}>
              <div className="relative pb-8">
                {idx !== resource.ResourceActivity.length - 1 && (
                  <span
                    className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                    aria-hidden="true"
                  />
                )}
                <div className="relative flex space-x-3">
                  <div>
                    <span className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center ring-8 ring-white">
                      <ArrowPathIcon className="h-5 w-5 text-white" aria-hidden="true" />
                    </span>
                  </div>
                  <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                    <div>
                      <p className="text-sm text-gray-900">
                        {activity.description}{' '}
                        <span className="font-medium text-gray-900">
                          by {activity.User.firstName} {activity.User.lastName}
                        </span>
                      </p>
                    </div>
                    <div className="whitespace-nowrap text-right text-sm text-gray-500">
                      {formatDateTime(activity.performedAt)}
                    </div>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl">
                {loading ? (
                  <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : resource ? (
                  <>
                    {/* Header */}
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center">
                          <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                            <ServerIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
                          </div>
                          <div className="ml-4">
                            <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                              {resource.name}
                            </Dialog.Title>
                            <div className="mt-1 flex items-center space-x-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(resource.status)}`}>
                                {getStatusIcon(resource.status)}
                                <span className="ml-1">{resource.status}</span>
                              </span>
                              {resource.Engagement && (
                                <span className="text-sm text-gray-500">
                                  • {resource.Engagement.name}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                          onClick={onClose}
                        >
                          <span className="sr-only">Close</span>
                          <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                        </button>
                      </div>
                    </div>

                    {/* Tabs */}
                    <div className="border-b border-gray-200">
                      <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
                        <button
                          onClick={() => setActiveTab('details')}
                          className={`${
                            activeTab === 'details'
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm`}
                        >
                          Details
                        </button>
                        <button
                          onClick={() => setActiveTab('activity')}
                          className={`${
                            activeTab === 'activity'
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm`}
                        >
                          Activity
                        </button>
                      </nav>
                    </div>

                    {/* Content */}
                    <div className="px-4 py-5 sm:p-6 max-h-96 overflow-y-auto">
                      {activeTab === 'details' && renderDetails()}
                      {activeTab === 'activity' && renderActivity()}
                    </div>

                    {/* Footer */}
                    <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                      <button
                        type="button"
                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                        onClick={onClose}
                      >
                        Close
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="p-6 text-center text-gray-500">
                    Failed to load resource details
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ResourceDetailModal;